import { API_URL } from '@constants';
import { ApiError, useApiConfig } from '@hooks/useApiConfig';
import { useState } from 'react';
import { useData } from '@hooks/useData';
import { DocumentsStep } from './../types/documentsStep';

export type RegistrationDocumentUploadInput = {
    uuid: string;
    documents: { type: string; file: File }[];
    language?: string;
};

export type RegistrationDocumentStepSuccessResponse = {
    success: boolean;
    message: string;
    documents: {
        CC: string;
        INS: string;
        DUA: string;
    };
};

export const useRegistrationDocumentUploadStep = () => {
    const { handleCommonErrors } = useApiConfig();

    const [loading, setLoading] = useState<boolean>(false);
    const [error, setError] = useState<string | null>(null);
    // for errors associated with form fields (422 Unprocessable Content)
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const [validationErrors, setValidationErrors] = useState<any | null>(null);

    const [lastResponseTime, setLastResponseTime] = useState<Date | null>(null);
    const { setDocumentStep } = useData();

    const request = async (props: RegistrationDocumentUploadInput) => {
        setLoading(true);
        setError(null);
        setValidationErrors(null);

        try {
            const formData = new FormData();
            if (props.language) formData.append('language', props.language);

            props.documents.forEach((doc, index) => {
                formData.append(`documents[${index}][type]`, doc.type);
                formData.append(`documents[${index}][file]`, doc.file);
            });

            const response = await fetch(`${API_URL}/draft-registration/step-3/${props.uuid}`, {
                method: 'POST',
                body: formData,
            });

            if (!response.ok) {
                const error = new Error() as ApiError;

                try {
                    const json = await response.json();
                    error.message = json.message;
                    error.errors = json.errors;
                } catch (error: any) {
                    error.message = '';
                }

                error.statusCode = response.status;

                throw error;
            }

            const rawResponse = await response.json();
            const data = rawResponse.data;
            const mappedStep: DocumentsStep = {
                CC: data.documents.CC || null,
                INS: data.documents.INS || null,
                DUA: data.documents.DUA || null,
            };

            setDocumentStep?.(mappedStep);
        } catch (error: any) {
            handleCommonErrors(error);
            error.message && setError(error.message);
            error.errors && setValidationErrors(error.errors);
        } finally {
            setLoading(false);
            setLastResponseTime(new Date());
        }
    };

    return { request, loading, error, validationErrors, lastResponseTime };
};
